import React, {createContext, useContext, useState, useEffect, useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import useCompanyShareDetails from "../../hooks/useCompanyShareDetails";
import {shareMarketplaceAPI} from "../../common/api/shareMarketplaceAPI";
import {
    API_DIRECT_SALES_PRICING,
    API_SHARE_MARKETPLACE_OFFERS,
    API_SHARE_MARKETPLACE_SHARES_AVERAGE_PRICE,
    API_SHARE_MARKETPLACE_SHARES_MEDIAN_PRICE
} from "../../common/api/apiConstants";
import {createURLQuery} from "../../common/api/apiUtil";
import {ANNUAL_CHARGE_CURRENCY, SHARES_CURRENCY} from "../../common/constant";
import {dateToURLDateString, dateToUTCTimezone} from "../../common/date";
import {CurrencyContext} from "./CurrencyProvider";
import {useCurrencyConversion, useKeycloakFunctions} from "../../hooks";
import {userAccountsAPI} from "../../common/api";

const FALLBACK_PRICE_FOR_DIRECT = 2200; // CHF

const ShareContext = createContext();

export const useMarketplaceShareInfo = () => {
    return useContext(ShareContext);
};

export const MarketPlaceShareInfoProvider = ({ children }) => {
    const {companyShare, loading: companyShareLoading} = useCompanyShareDetails();
    const {getCurrencyConversion} = useCurrencyConversion();
    const {currency} = useContext(CurrencyContext)?.[0];
    const {authenticated} = useKeycloakFunctions();

    // Internal state
    const [directSalesData, setDirectSalesData] = useState();

    // Exposed state
    const [directSalesDataLoading, setDirectSalesDataLoading] = useState(false);
    const [directSalesDataError, setDirectSalesDataError] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [shareData, setShareData] = useState({
        averagePrice: { original_price: null, converted_price: null, original_price_currency: "", converted_price_currency: "" },
        medianPrice: { original_price: null, converted_price: null, original_price_currency: "", converted_price_currency: "" },
        directSalesPrice: { original_price: null, converted_price: null, original_price_currency: "", converted_price_currency: "" },
        annualChargePrice: { original_price: null, converted_price: null, original_price_currency: "", converted_price_currency: "" }
    });

    const nowMinusNinetyDays = () => {
        const today = new Date();
        const res = new Date(today.setDate(today.getDate() - 90));
        return res;
    };

    // Helper functions
    const fetchAveragePrice = useCallback(async (params) => {
        const value = (await shareMarketplaceAPI.get(API_SHARE_MARKETPLACE_OFFERS
            + API_SHARE_MARKETPLACE_SHARES_AVERAGE_PRICE
            + createURLQuery(params ?? {
                status: "finalised",
                transfered_at_start: dateToURLDateString(dateToUTCTimezone(nowMinusNinetyDays())),
                transfered_at_end: dateToURLDateString(dateToUTCTimezone(new Date()))
            }))).data;

        return value;
    }, []);

    const fetchMedianPrice = useCallback(async (currency = SHARES_CURRENCY) => {
        const value = (await shareMarketplaceAPI.get(API_SHARE_MARKETPLACE_OFFERS
            + API_SHARE_MARKETPLACE_SHARES_MEDIAN_PRICE
            + createURLQuery({
                currency,
                status: "active"
            })
        )).data;

        return value;
    }, []);

    const fetchDirectSalesPricing = useCallback(async () => {
        if (currency) {
            const res = await userAccountsAPI.get(`${API_DIRECT_SALES_PRICING}?currency=${currency}`);
            return res?.data;
        }
    }, [currency]);

    const getDirectSalesPrice = useCallback(async (currency, averagePrice) => {
        let basePriceForDirect = null;
        let convertedPriceForDirect = null;

        // Calculate first base price for direct sales
        if (companyShare?.price?.[ANNUAL_CHARGE_CURRENCY]) {
            // If direct CHF pirce exist in contentful pick that
            basePriceForDirect = companyShare.price[ANNUAL_CHARGE_CURRENCY];
        }
        else if (averagePrice) {
            if (averagePrice > 0) {
                // Check if we need to load base price for direct purchase
                const res = await getCurrencyConversion([
                    {
                        label: "price_for_direct",
                        from_currency: SHARES_CURRENCY,
                        to_currency: ANNUAL_CHARGE_CURRENCY,
                        convert_amount: averagePrice,
                    },
                ]);

                const convertedValue = res?.[0]?.result_amount;
                basePriceForDirect = convertedValue >= 0
                    ? convertedValue > FALLBACK_PRICE_FOR_DIRECT
                        ? convertedValue + 500
                        : FALLBACK_PRICE_FOR_DIRECT
                    : null;
            } else {
                // If value in contenful is missing and rolling average is not suficient
                // set the value directly
                basePriceForDirect = null;
            }
        }

        if (basePriceForDirect && currency !== ANNUAL_CHARGE_CURRENCY) {
            if (companyShare?.price?.[currency]) {
                convertedPriceForDirect = companyShare.price[currency];
            } else {
                const res = await getCurrencyConversion([{
                    label: "converted_price_for_direct",
                    from_currency: ANNUAL_CHARGE_CURRENCY,
                    to_currency: currency,
                    convert_amount: basePriceForDirect
                }]);
                convertedPriceForDirect = res?.[0]?.result_amount;
            }
        }

        return {
            original_price: basePriceForDirect,
            original_price_currency: ANNUAL_CHARGE_CURRENCY,
            converted_price: convertedPriceForDirect,
            converted_price_currency: currency
        };

    }, [JSON.stringify(companyShare)]);

    const getAnnualChargePrice = useCallback(async (currency) => {
        const annualChargeCHF = companyShare?.annualCharge;
        let convertedAnnualPrice = null;

        if (currency !== ANNUAL_CHARGE_CURRENCY) {
            const res = await getCurrencyConversion([{
                label: "annual_charge",
                from_currency: ANNUAL_CHARGE_CURRENCY,
                to_currency: currency,
                convert_amount: annualChargeCHF
            }]);

            convertedAnnualPrice = res?.[0]?.result_amount;
        }

        const data = {
            original_price: annualChargeCHF,
            original_price_currency: ANNUAL_CHARGE_CURRENCY,
            converted_price: convertedAnnualPrice,
            converted_price_currency: convertedAnnualPrice ? currency : null
        };

        return data;
    }, [JSON.stringify(companyShare)]);

    // Exposed functions
    const getDirectSalesPricingData = useCallback(async (numOfShares = 1) => {
        const prices = directSalesData
            ?.pricing_chf
            ?.[numOfShares.toString()];

        const data = {
            promotionInfo: {
                isPointsPromoActive: directSalesData?.number_of_points_promotion > 0,
                isSharesPromotionActive: directSalesData?.share_discount_percentage_promo > 0,
                isAnnualChargePromotionActive: directSalesData?.annual_fee_discount_percentage_promo > 0,
                name: directSalesData?.promotion_name,
                numOfPoints: directSalesData?.number_of_points_promotion,
                startDate: directSalesData?.promotion_start_date,
                endDate: directSalesData?.promotion_end_date,
                shareDiscountPercentage: directSalesData?.share_discount_percentage_promo,
                annualChargeDiscountPercentage: directSalesData?.annual_fee_discount_percentage_promo,
                pointsPromotionName: directSalesData?.points_promotion_name,
            },
            prices: {
                originalCurrency: ANNUAL_CHARGE_CURRENCY,
                convertedCurrency: currency,
                loyaltyDiscountPercentage: prices?.loyalty_discounts,
                numOfShares: prices?.total_number_of_shares,
                total: {
                    originalPrice: prices?.total_chf,
                },
                share: {
                    originalPrice: prices?.total_share_price_chf,
                    originalPromoPrice: prices?.total_share_price_promo_chf,
                },
                annualCharge: {
                    originalPrice: prices?.total_annual_fee_chf,
                    originalPromoPrice: prices?.total_annual_fee_promo_chf,
                }
            }
        };

        return data;
    }, [directSalesData, currency]);

    // Loader functions
    const loadData = async () => {
        try {
            setError(null);
            setLoading(true);

            // Fetch avg and median prices from marketplace
            const averagePrice = await fetchAveragePrice();
            const medianPrice = await fetchMedianPrice(currency);
            const directSalesPrice = await getDirectSalesPrice(currency, averagePrice);
            const annualChargePrice = await getAnnualChargePrice(currency);
            const data = {
                averagePrice,
                medianPrice,
                directSalesPrice,
                annualChargePrice
            };

            setShareData(data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const loadDirectSalesPricing = async () => {
        setDirectSalesDataLoading(true);
        try {
            // Fetch direct shares pricing model
            const data = await fetchDirectSalesPricing();
            setDirectSalesData(data);
        } catch (err) {
            setDirectSalesDataError(err);
        } finally {
            setDirectSalesDataLoading(false);
        }
    };

    useEffect(() => {
        if (!companyShareLoading && currency) {
            loadData();
        }

    }, [companyShareLoading, currency]);

    // For direct sales pricing
    useEffect(() => {
        if (authenticated && currency) {
            loadDirectSalesPricing();
        }
    }, [authenticated, currency]);


    const memoVal = useMemo(() => ({
        getDirectSalesPricingData,
        shareData,
        loading,
        directSalesDataLoading,
        error,
        directSalesDataError
    }), [
        loading,
        shareData,
        error,
        getDirectSalesPricingData,
        directSalesDataLoading,
        directSalesDataError
    ]);

    return (
        <ShareContext.Provider value={memoVal}>
            {children}
        </ShareContext.Provider>
    );
};

MarketPlaceShareInfoProvider.propTypes = {
    children: PropTypes.any
};
